import React from 'react';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  GridSize,
} from '@material-ui/core';
import clsx from 'clsx';
import { DisplayItem, RadioDisplayItem } from '../../../types';
import { BlueFormLabel, generateProperties } from '../helpers';
import { FormHookResult } from '../../../validation';
import colors from '../../../theme/colors';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    check: {
      width: 24,
      height: 24,
      marginTop: 2,
      marginRight: 4,
    },
    radioLabel: {
      marginTop: theme.spacing(1),
      marginLeft: 0,
      fontsize: 60,
    },
    disabledElement: {
      color: `${colors.silver} !important`,
    },
    disabledElementText: {
      color: colors.abbey,
    },
    checkDecoratorWrapper: {
      marginLeft: 28,
    },
    contentWithGutter: {
      marginLeft: theme.spacing(2),
    },
  }),
);

export type RadioButtonsGroupProps = {
  disabled?: boolean;
  formTitle: string;
  gridSize?: GridSize;
  defaultValue?: string;
  onChange?: (val: DisplayItem) => void;
  radioData: RadioDisplayItem[];
  radioGroupName: string;
  boldLabel?: boolean;
  disabledValues?: string[];
  errorMessage?: string;
  gutterLeft?: boolean;
  centerLabels?: boolean;
  dataSource?: FormHookResult<string, DisplayItem>;
  inputId?: string;
  labelStyle?: React.CSSProperties;
  formControlStyle?: React.CSSProperties;
};

const RadioButtonsGroup = ({
  formTitle,
  radioData,
  radioGroupName,
  onChange,
  gridSize = 3,
  defaultValue = '',
  disabled = false,
  boldLabel = false,
  disabledValues = [],
  errorMessage = '',
  gutterLeft = false,
  centerLabels = false,
  dataSource,
  inputId,
  labelStyle,
  formControlStyle,
}: RadioButtonsGroupProps): JSX.Element => {
  const formInputProps = generateProperties<string, DisplayItem>({
    inputName: radioGroupName,
    onChange,
    defaultValue,
    inputErrorMessage: errorMessage,
    dataSource,
  });

  const classes = useStyles();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const valueOfRadioButton = (event.target as HTMLInputElement).value;
    const result = radioData
      .filter((item: DisplayItem): boolean => item.Value === valueOfRadioButton)
      .pop();
    if (typeof result !== 'undefined') {
      formInputProps.onChange(result);
    }
  };

  function optionIsDisabled(optionValue: string): boolean {
    return disabledValues.includes(optionValue) || disabled;
  }

  return (
    <FormControl
      component="fieldset"
      fullWidth
      disabled={disabled}
      error={formInputProps.errorText !== ''}
      style={formControlStyle}
    >
      <BlueFormLabel data-testid="blueformlabel" bold={boldLabel}>
        {formTitle}
      </BlueFormLabel>
      <RadioGroup
        id={inputId}
        name={formInputProps.name}
        value={formInputProps.value}
        onChange={handleChange}
      >
        <Grid
          data-testid="container"
          container
          justifyContent={centerLabels ? 'center' : 'flex-start'}
          className={gutterLeft ? classes.contentWithGutter : ''}
        >
          {radioData.map(
            (radioInfo: RadioDisplayItem, index: number): React.ReactNode => (
              <Grid item xs={gridSize} key={radioInfo.Value} container>
                <Grid item xs={12}>
                  <FormControlLabel
                    className={classes.radioLabel}
                    value={radioInfo.Value}
                    control={
                      <Radio
                        id={`${inputId}-${radioInfo.inputId || index}`}
                        className={clsx(
                          classes.check,
                          optionIsDisabled(radioInfo.Value)
                            ? classes.disabledElement
                            : '',
                        )}
                        disableRipple
                      />
                    }
                    label={
                      <Typography
                        variant="caption"
                        color="primary"
                        className={clsx(
                          classes.radioLabel,
                          optionIsDisabled(radioInfo.Value)
                            ? classes.disabledElementText
                            : '',
                        )}
                        style={labelStyle}
                      >
                        {radioInfo.Text}
                      </Typography>
                    }
                    disabled={optionIsDisabled(radioInfo.Value)}
                  />
                </Grid>
                {radioInfo.decorator && radioInfo.Value === defaultValue && (
                  <Grid data-testid="radio-decorator" item xs={12}>
                    <div
                      className={classes.checkDecoratorWrapper}
                      style={radioInfo.decoratorStyle}
                    >
                      {radioInfo.decorator}
                    </div>
                  </Grid>
                )}
              </Grid>
            ),
          )}
        </Grid>
      </RadioGroup>
      <FormHelperText data-testid={`form-helper-text-${radioGroupName}`}>
        {formInputProps.errorText}
      </FormHelperText>
    </FormControl>
  );
};

export default RadioButtonsGroup;
